import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Checkbox, FormControlLabel, Stack, Tooltip } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { PortfolioAddOrUpdateModel } from "../types/PortfolioAddOrUpdateModel";
import {
    ActionConfirmationDialog
} from '../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog';
import {
    useActionConfirmationDialog
} from "../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import {
    useAppSidePanelContextProvider
} from "../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { DraggableDialogPaper } from "../../../screens/positions-and-orders/dialogs/DraggableDialogPaper";
import { DraggableDialogTitle } from "../../../screens/positions-and-orders/dialogs/DraggableDialogTitle";
import { NumericField } from "../../../screens/positions-and-orders/controls";
import { ApplicationDateTimePicker } from "../../../shared/components/date-time-picker/ApplicationDateTimePicker";
import { dateTimeConversionService } from "../../../shared/services/DateTimeConversionService";
import { ApplicationTooltip } from "../../../screens/positions-and-orders/views";
import { numberFormatters } from "../../../screens/shared/utils/numberFormatters";

const defaultMarginBuffer = 10000;

export function PortfolioManagerDialog({
    isOpen,
    setIsOpen,
    isCreation,
    submitAction,
    editingPortfolioModel,
    icon,
}: {
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    isCreation: boolean,
    submitAction: ((portfolio: PortfolioAddOrUpdateModel) => void) | undefined,
    editingPortfolioModel?: PortfolioAddOrUpdateModel | null,
    icon: React.ReactNode;
}) {
    const {
        deleteSelectedPortfolio,
    } = useAppSidePanelContextProvider();
    
    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();

    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [marginBuffer, setMarginBuffer] = useState<number>(defaultMarginBuffer);
    const [inceptionDate, setInceptionDate] = useState<Dayjs | null>(dayjs());
    const [useValuationDate, setUseValuationDate] = useState<boolean>(false);
    const [valuationDate, setValuationDate] = useState<Dayjs | null>(dayjs());

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const currentDate = getCurrentUtcDisplayDate();
        const initialInceptionDate = isCreation
            ? currentDate
            : dateTimeConversionService.convertUtcDateToDisplayDate(
                editingPortfolioModel?.inceptionDate ?? currentDate.toDate());
        const initialValuationDate = isCreation
            ? initialInceptionDate
            : dateTimeConversionService.convertUtcDateToDisplayDate(
                editingPortfolioModel?.valuationDate ?? initialInceptionDate.toDate());

        setName(isCreation ? '' : editingPortfolioModel?.name ?? '');
        setDescription(isCreation ? '' : editingPortfolioModel?.description ?? '');
        setMarginBuffer(isCreation
            ? defaultMarginBuffer
            : editingPortfolioModel?.marginBuffer ?? defaultMarginBuffer);
        setInceptionDate(initialInceptionDate);
        setUseValuationDate(isCreation
            ? false
            : editingPortfolioModel?.useValuationDate ?? false);
        setValuationDate(initialValuationDate);
    }, [editingPortfolioModel, isCreation, isOpen]);
    
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isDateConfigurationValid() || inceptionDate === null || valuationDate === null) {
            return;
        }

        const portfolioModel = {
            name,
            description,
            marginBuffer,
            inceptionDate: dateTimeConversionService.toUtcDate(inceptionDate),
            useValuationDate,
            valuationDate: dateTimeConversionService.toUtcDate(valuationDate),
        };
        
        const action = () => {
            if (submitAction) {
                submitAction(portfolioModel);
            }
        }
            
        if (isCreation){
            action();
            handleClose();
        }
        else {
            setConfirmedAction(() => action);
            setConfirmationMessage("Are you sure you want to update the portfolio?");
            setSubmitButtonName("Update");
            setIsActionConfirmationDialogOpen(true); 
        }
    };

    const handleInceptionDateChanging = (newValue: Dayjs | null): void => {
        setInceptionDate(newValue);

        if (!useValuationDate &&
            newValue !== null &&
            (valuationDate === null || newValue.isAfter(valuationDate))) {
            setValuationDate(newValue);
        }
    };

    const handleUseValuationDateChanging = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const isEnabled = event.target.checked;
        setUseValuationDate(isEnabled);

        if (inceptionDate !== null &&
            (valuationDate === null || valuationDate.isBefore(inceptionDate))) {
            setValuationDate(inceptionDate);
        }
    };

    const isDateConfigurationValid = (): boolean =>
        inceptionDate !== null &&
        (!useValuationDate ||
            (valuationDate !== null && !valuationDate.isBefore(inceptionDate)));

    const getCurrentUtcDisplayDate = (): Dayjs =>
        dateTimeConversionService.convertUtcDateToDisplayDate(new Date());
    
    function getDialogTitle() {
        return isCreation
            ? 'Create portfolio'
            : 'Edit portfolio';
    }

    function getSubmitButtonCaption() {
        return isCreation
            ? 'Create'
            : 'Update';
    }
    
    const handleForceDelete = () => {
        setConfirmedAction(() => () => deleteSelectedPortfolio(true));
        setConfirmationMessage("Are you sure you want to force delete the portfolio?");
        setSubmitButtonName("Delete");
        setIsActionConfirmationDialogOpen(true);
    }
    
    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                confirmedParentAction={confirmedAction}
                submitButtonName={submitButtonName}
                doAfterConfirmation={handleClose}
            />
            <Dialog
                open={isOpen}
                onClose={handleClose}
                PaperComponent={DraggableDialogPaper}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleSubmit,
                }}
            >
                <DraggableDialogTitle
                    icon={icon}
                    title={getDialogTitle()}
                    onClose={handleClose}
                />
                <DialogContent>
                    <Stack
                        direction="column"
                        spacing={2}
                        alignItems="flex-start"
                        sx={{ width: 500 }}
                    >
                        <TextField
                            autoFocus
                            required
                            id="name"
                            name="name"
                            label="Name"
                            type="text"
                            variant="standard"
                            size="small"
                            value={name}
                            onChange={event => setName(event.target.value)}
                            sx={{
                                width: 500,
                                margin: 0,
                                padding: 0,
                            }}
                        />
                        <Stack direction="row" spacing={2}>
                            <ApplicationTooltip
                                title="Assumed adverse price movement used to estimate the capital required to maintain futures positions"
                                placement="right-start"
                                disableInteractive
                            >
                                <NumericField
                                    id="marginBuffer"
                                    name="marginBuffer"
                                    label="Margin buffer"
                                    value={marginBuffer}
                                    onValueChange={setMarginBuffer}
                                    width={80}
                                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                    step={1}
                                />
                            </ApplicationTooltip>
                            <ApplicationDateTimePicker
                                label="Inception date"
                                value={inceptionDate}
                                onChange={handleInceptionDateChanging}
                                width={210}
                                tooltipTitle="Portfolio start date used for performance calculations"
                                tooltipPlacement="right-start"
                                slotProps={{
                                    textField: {
                                        required: true,
                                    },
                                }}
                            />
                        </Stack>
                        <Stack direction="column" spacing={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                        color="default"
                                        id="useValuationDate"
                                        name="useValuationDate"
                                        checked={useValuationDate}
                                        onChange={handleUseValuationDateChanging}
                                    />
                                }
                                label="Use custom valuation date"
                            />
                            <ApplicationDateTimePicker
                                label="Valuation date"
                                value={valuationDate}
                                onChange={setValuationDate}
                                minDateTime={useValuationDate && inceptionDate !== null
                                    ? inceptionDate
                                    : undefined}
                                width={210}
                                disabled={!useValuationDate}
                                tooltipTitle="Date on which all positions are assumed to be valued and closed for performance calculation purposes"
                                tooltipPlacement="right-start"
                                slotProps={{
                                    textField: {
                                        required: useValuationDate,
                                    },
                                }}
                            />
                        </Stack>
                        <TextField
                            margin="normal"
                            id="description"
                            name="description"
                            label="Description"
                            type="string"
                            size="small"
                            value={description}
                            onChange={event => setDescription(event.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        size="small"
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!isDateConfigurationValid()}
                    >
                        {getSubmitButtonCaption()}
                    </Button>
                    {!isCreation &&
                        <Tooltip
                            arrow
                            title="Delete the portfolio even it contains locked positions"
                            slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                        >
                            <Button
                                sx={{ fontWeight: "bold" }}
                                size="small"
                                variant="contained"
                                color="error"
                                onClick={handleForceDelete}
                            >
                                Force delete
                            </Button>
                        </Tooltip>
                    }
                </DialogActions>
            </Dialog>
        </>
    );
}
